<template>
  <SectionCard
    :title="domain.isMonthly ? 'Monthly portfolios' : '2026 annual portfolio'"
    :note="
      domain.isMonthly
        ? 'Each consumption month is its own portfolio. A certificate earmarked to one month cannot move to another, so a surplus month never offsets a short month. Select a month to filter the certificates below; the expand icon opens its detail.'
        : undefined
    "
  >
    <MonthlyPortfoliosGrid
      v-if="domain.months"
      :months="domain.months"
      :selected="filterMonth"
      @select="onSelect"
      @open="$emit('open-month', $event)"
    />
    <AnnualPortfolioBars v-else :domain />

    <div class="mt-4">
      <PortfolioItemsTable
        :items="rows"
        :disableSelection="true"
        :hideActionBar="true"
        :defaultVisibleColumns="COLUMNS"
        storageContext="compliance-groups-domain"
      >
        <!-- The title shares the row with the column selector, so it sits on the table. -->
        <template #header-start>
          <div class="d-flex align-items-center flex-wrap gap-3">
            <h2 class="cg-section-title">Certificates</h2>
            <span
              v-if="filterMonth !== null"
              class="badge bg-primary-subtle text-primary-emphasis border border-primary d-inline-flex align-items-center gap-2"
            >
              France · {{ MONTHS[filterMonth] }} 2026
              <button
                type="button"
                class="btn btn-sm btn-link p-0 text-primary-emphasis"
                aria-label="Clear portfolio filter"
                @click="store.setCertMonthFilter(null)"
              >
                <font-awesome-icon icon="xmark" />
              </button>
            </span>
          </div>
        </template>
      </PortfolioItemsTable>
      <p v-if="rows.length === 0" class="text-body-secondary small mt-2 mb-0">
        Nothing allocated to this portfolio yet.
      </p>
      <p v-else class="cg-tabular small mt-2 mb-0">
        <strong>{{ fmt(total) }}</strong
        >&nbsp;<span class="text-body-secondary">MWh</span>
      </p>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { toFlattenedPortfolioItems } from "@/compliance_groups/fixture/adapters";
import {
  francePortfolioName,
  lotsForPortfolio,
} from "@/compliance_groups/fixture/allocator";
import { MONTHS } from "@/compliance_groups/fixture/complianceGroupsFixture";
import type { DerivedDomain } from "@/compliance_groups/fixture/derive";
import { fmt, sum } from "@/compliance_groups/fixture/format";
import { useComplianceDemoStore } from "@/compliance_groups/useComplianceDemoStore";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import type { ColumnKey } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { computed } from "vue";
import AnnualPortfolioBars from "./AnnualPortfolioBars.vue";
import MonthlyPortfoliosGrid from "./MonthlyPortfoliosGrid.vue";
import SectionCard from "./SectionCard.vue";

const COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "status",
  "portfolio_name",
  "registry_region",
  "production_device_name",
  "transfer_deadline",
];

const props = defineProps<{ domain: DerivedDomain }>();
const emit = defineEmits<{ "open-month": [monthIndex: number] }>();

const store = useComplianceDemoStore();
const filterMonth = computed(() =>
  props.domain.key === "fr" ? store.state.certMonthFilter : null
);
const lots = computed(() =>
  filterMonth.value === null
    ? props.domain.lots
    : lotsForPortfolio(
        props.domain.lots,
        francePortfolioName(filterMonth.value)
      )
);
const rows = computed(() => toFlattenedPortfolioItems(lots.value));
const total = computed(() => sum(lots.value.map((l) => l.n)));

// Selecting a month filters the table; selecting it again clears. A month with no certificates has
// nothing to filter, so it opens the detail modal instead.
const onSelect = (monthIndex: number) => {
  const card = props.domain.months?.find((m) => m.m === monthIndex);
  if (card && !card.hasLots) return emit("open-month", monthIndex);
  store.toggleCertMonthFilter(monthIndex);
};
</script>
